import { useState, useMemo, useRef, useEffect, Fragment } from "react";
import { usePageTracking } from "@/hooks/use-track";
import { HelpTip } from "@/components/help-tip";
import { usePageTour } from "@/components/ProductTour";
import { TutorialTip } from "@/components/TutorialTip";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { EmptyState } from "@/components/empty-state";
import { TableSkeleton } from "@/components/loading-skeleton";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  BookOpen, Plus, Search, Filter, Edit2, RotateCcw, ChevronRight,
  ChevronDown, X, Save, Layers, EyeOff, Undo2, Trash2, SlidersHorizontal,
  UploadCloud, Loader2, HardHat,
} from "lucide-react";
import { COST_CATEGORIES, COST_TRADES, UNITS, getUnits, getCostCategories, getCostTrades, type CostLibraryTask } from "@shared/schema";
import { formatMoney } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { type CostNavItem, type CostNavSection } from "@/lib/cost-library-nav";
import { getCostTaskBrowseLocation, matchesCostNavItem, matchesCostNavSection } from "@/lib/cost-library-membership";
import { buildCostLibraryNavigation } from "@/lib/cost-library-navigation";
import { useCostLibrary } from "@/hooks/use-cost-library";
import { CategorySidebar, useOrderedSections, getNavSectionDisplayLabel } from "@/components/category-sidebar";
import { NavigationHeadingNameDialog } from "@/components/quote-heading-preferences";
import { useQuoteHeadingPreferences } from "@/hooks/use-quote-heading-preferences";
import { getNavHeadingEntry, getNavItemDisplayLabel, type NavHeadingEntry } from "@workspace/api-zod/quote-headings";
import { CostRecipePanel } from "@/components/cost-recipe-panel";
import { CostRecipeRateConfirmation, type RateConfirmationRequest } from "@/components/cost-recipe-rate-confirmation";
import { StackedBricksIcon, TowerCraneIcon } from "@/components/cost-section-icons";
import { COST_SECTION_LABELS, type CostSection } from "@/components/cost-breakdown-table";
import { isUkRecipeMarket, isRecipeBacked, allowsGenericOverrideReset } from "@/components/cost-recipe-rules";
import { getCostTaskDisplayLabels } from "@/lib/cost-library-labels";

function getUnitLabel(value: string): string {
  return (UNITS as Record<string, string>)[value] || value;
}

function getCategoryLabel(value: string, market?: string | null): string {
  const cats = getCostCategories(market);
  return cats.find(c => c.value === value)?.label || value;
}

function getTradeLabel(category: string, trade: string, market?: string | null): string {
  const trades = getCostTrades(market);
  const cat = trades[category];
  if (!cat) return trade;
  return cat.trades.find(t => t.value === trade)?.label || trade;
}

function useFormatCurrency() {
  const { user } = useAuth();
  return (value: string | number) => formatMoney(value, user?.market);
}

interface PushLiveResult {
  dryRun: boolean;
  updates: number;
  inserts: number;
  deactivates: number;
  skippedNewerOnTarget: number;
  conflicts?: number;
  conflictDetails?: { name: string; market: string; reason: string }[];
  unchanged?: number;
  planToken?: string;
  updateDetails: { name: string; market: string; changedFields: string[]; oldRateTypical: string; newRateTypical: string }[];
  insertDetails: { name: string; market: string; rateTypical: string }[];
  deactivateDetails: { name: string; market: string }[];
}

const overrideFormSchema = z.object({
  rateLow: z.string().min(1, "Required"),
  rateTypical: z.string().min(1, "Required"),
  rateHigh: z.string().min(1, "Required"),
  changeReason: z.string().optional(),
});

const customTaskSchema = z.object({
  category: z.string().min(1, "Category is required"),
  trade: z.string().min(1, "Trade is required"),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  unit: z.string().min(1, "Unit is required"),
  rateLow: z.string().min(1, "Required"),
  rateTypical: z.string().min(1, "Required"),
  rateHigh: z.string().min(1, "Required"),
  keywords: z.string().optional(),
  notes: z.string().optional(),
});


export default function CostLibraryPage() {
  usePageTracking("Cost Library");
  usePageTour("cost-library");
  const formatCurrency = useFormatCurrency();
  const { user: marketUser } = useAuth();
  const COST_CATEGORIES = getCostCategories(marketUser?.market);
  const COST_TRADES = getCostTrades(marketUser?.market);
  const UNIT_OPTIONS = Object.entries(getUnits(marketUser?.market)).map(([value, label]) => ({ value, label }));
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedTrade, setSelectedTrade] = useState<string>("all");
  const [navKeyword, setNavKeyword] = useState<string>("");
  const [unitFilter, setUnitFilter] = useState<string>("all");
  const [editingTask, setEditingTask] = useState<CostLibraryTask | null>(null);
  const [rateConfirmation, setRateConfirmation] = useState<RateConfirmationRequest | null>(null);
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  // Stable identity of the selected nav section (null = none / fallback category).
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  // Label for a plain category filter that has no nav section (not an identity).
  const [fallbackSectionLabel, setFallbackSectionLabel] = useState<string>("");
  const [expandedBreakdowns, setExpandedBreakdowns] = useState<Record<string, CostSection | undefined>>({});
  const isUkMarket = isUkRecipeMarket(marketUser?.market);
  function toggleBreakdown(key: string, section: CostSection) {
    setExpandedBreakdowns(prev => {
      return { ...prev, [key]: prev[key] === section ? undefined : section };
    });
  }
  const [editingHeading, setEditingHeading] = useState<NavHeadingEntry | null>(null);
  const { names: headingNames } = useQuoteHeadingPreferences();
  const { toast } = useToast();
  const { data: tasks, isLoading, error: libraryError, refetch: refetchLibrary } = useCostLibrary();
  const navigation = useMemo(() => buildCostLibraryNavigation(tasks ?? [], marketUser?.market), [tasks, marketUser?.market]);
  const { sections: orderedSections, reorder: reorderSections } = useOrderedSections(navigation);

  const { data: adminData } = useQuery<{ isAdmin: boolean }>({
    queryKey: ["/api/admin/check"],
    retry: false,
  });
  const isAdmin = adminData?.isAdmin === true;
  const [showPushLiveDialog, setShowPushLiveDialog] = useState(false);
  const [pushPreview, setPushPreview] = useState<PushLiveResult | null>(null);

  const overrideForm = useForm({
    resolver: zodResolver(overrideFormSchema),
    defaultValues: { rateLow: "", rateTypical: "", rateHigh: "", changeReason: "" },
  });

  const customForm = useForm({
    resolver: zodResolver(customTaskSchema),
    defaultValues: {
      category: "", trade: "", name: "", description: "", unit: "each",
      rateLow: "", rateTypical: "", rateHigh: "", keywords: "", notes: "",
    },
  });

  const [customNavSection, setCustomNavSection] = useState("");
  const selectedCategoryForCustom = customForm.watch("category");

  const saveMutation = useMutation({
    mutationFn: (data: { taskId: string; rateLow: string; rateTypical?: string; rateHigh: string; changeReason?: string }) =>
      apiRequest("POST", "/api/cost-library/overrides", {
        taskId: data.taskId,
        rateLow: data.rateLow,
        rateTypical: data.rateTypical,
        rateHigh: data.rateHigh,
        changeReason: data.changeReason,
        enabled: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cost-library"] });
      toast({ title: "Rate saved as your default" });
      setEditingTask(null);
      overrideForm.reset();
    },
    onError: () => {
      toast({ title: "Failed to save rate", variant: "destructive" });
    },
  });

  // Admin edits to a default task write the central Admin Rate (the default
  // used by every user) instead of a personal "My Rate" override. If the
  // admin had an old personal override on the task, it is removed so the new
  // central rate actually takes effect for them too.
  const adminSaveMutation = useMutation({
    mutationFn: async (data: { taskId: string; rateLow: string; rateTypical?: string; rateHigh: string }) => {
      await apiRequest("PATCH", `/api/admin/cost-library/default/${data.taskId}`, {
        rateLow: data.rateLow,
        rateTypical: data.rateTypical,
        rateHigh: data.rateHigh,
        removeOwnOverride: !editingTask?.hasRecipeOverride,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cost-library"] });
      toast({ title: "Admin Rate saved", description: "This is now the central default rate for all users." });
      setEditingTask(null);
      overrideForm.reset();
    },
    onError: () => {
      toast({ title: "Failed to save admin rate", variant: "destructive" });
    },
  });

  const pushLiveMutation = useMutation({
    mutationFn: async (dryRun: boolean) => {
      const res = await apiRequest("POST", "/api/admin/cost-library/push-live", {
        dryRun, ...(!dryRun ? { expectedPlanHash: pushPreview?.planToken } : {}),
      });
      return (await res.json()) as PushLiveResult;
    },
    onSuccess: (result) => {
      setPushPreview(result);
      if (!result.dryRun) {
        toast({
          title: "Live site updated",
          description: `${result.updates} updated, ${result.inserts} added, ${result.deactivates} removed.`,
        });
      }
    },
    onError: (err: any) => {
      toast({ title: "Update Live failed", description: err?.message || "Could not reach the live site.", variant: "destructive" });
    },
  });

  const resetMutation = useMutation({
    mutationFn: (overrideId: string) =>
      apiRequest("DELETE", `/api/cost-library/overrides/${overrideId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cost-library"] });
      toast({ title: "Reset to default rate" });
      setEditingTask(null);
    },
    onError: () => {
      toast({ title: "Failed to reset rate", variant: "destructive" });
    },
  });

  const createCustomMutation = useMutation({
    mutationFn: (data: z.infer<typeof customTaskSchema>) =>
      apiRequest("POST", "/api/cost-library/custom", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cost-library"] });
      toast({ title: "Custom task added" });
      setShowCustomDialog(false);
      customForm.reset();
      setCustomNavSection("");
    },
    onError: () => {
      toast({ title: "Failed to add custom task", variant: "destructive" });
    },
  });

  const deleteCustomMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/cost-library/custom/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cost-library"] });
      toast({ title: "Custom task deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete task", variant: "destructive" });
    },
  });

  const updateCustomMutation = useMutation({
    mutationFn: (data: { id: string; rateLow: string; rateTypical: string; rateHigh: string }) =>
      apiRequest("PATCH", `/api/cost-library/custom/${data.id}`, {
        rateLow: data.rateLow,
        rateTypical: data.rateTypical,
        rateHigh: data.rateHigh,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cost-library"] });
      toast({ title: "Custom task updated" });
      setEditingTask(null);
      overrideForm.reset();
    },
    onError: () => {
      toast({ title: "Failed to update custom task", variant: "destructive" });
    },
  });

  const hideMutation = useMutation({
    mutationFn: ({ taskId, taskSource }: { taskId: string; taskSource: string }) =>
      apiRequest("POST", "/api/cost-library/hide", { taskId, taskSource }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cost-library"] });
      toast({ title: "Task hidden", description: "You can restore it from the bottom of the list." });
    },
    onError: () => {
      toast({ title: "Failed to hide task", variant: "destructive" });
    },
  });

  const unhideMutation = useMutation({
    mutationFn: (taskId: string) =>
      apiRequest("POST", "/api/cost-library/unhide", { taskId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cost-library"] });
      toast({ title: "Task restored" });
    },
    onError: () => {
      toast({ title: "Failed to restore task", variant: "destructive" });
    },
  });

  function toggleSection(sectionId: string) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }

  // From a global-search result, jump the left panel to the category the item
  // lives in (matching getNavLabels resolution) and clear the search so the
  // user lands in that category's normal browse view.
  function handleGoToTaskCategory(task: CostLibraryTask) {
    const target = getCostTaskBrowseLocation(task, orderedSections);
    setSearchQuery("");
    if (target) {
      handleSelectItem(target.item, target.section.id);
      setExpandedSections(prev => new Set(prev).add(target.section.id));
    } else {
      // No nav entry maps to this task's category — fall back to a plain
      // category filter so the item is still visible after the jump.
      setSelectedItemId(null);
      setSelectedCategory(task.category);
      setSelectedTrade("all");
      setNavKeyword("");
      setSelectedSectionId(null);
      setFallbackSectionLabel(getCategoryLabel(task.category, marketUser?.market));
    }
  }

  function handleSelectItem(item: CostNavItem, sectionId: string) {
    setSelectedItemId(item.id);
    setSelectedCategory(item.categoryFilter || "all");
    setSelectedTrade(item.tradeFilter || "all");
    setNavKeyword(item.keywordFilter || "");
    setSelectedSectionId(sectionId);
    setFallbackSectionLabel("");
  }

  function handleSelectSection(section: CostNavSection) {
    setSelectedItemId(null);
    setSelectedSectionId(section.id);
    setFallbackSectionLabel("");
    setSelectedTrade("all");
    const catSet = new Set<string>();
    section.items.forEach((i) => { if (i.categoryFilter) catSet.add(i.categoryFilter); });
    const cats = Array.from(catSet);
    if (cats.length === 1) {
      setSelectedCategory(cats[0]!);
      setNavKeyword("");
    } else {
      setSelectedCategory("all");
      const keywords = section.items
        .map((i) => i.keywordFilter)
        .filter(Boolean)
        .join(" ");
      setNavKeyword(keywords || "");
    }
  }

  function handleShowAll() {
    setSelectedItemId(null);
    setSelectedSectionId(null);
    setFallbackSectionLabel("");
    setSelectedCategory("all");
    setSelectedTrade("all");
    setNavKeyword("");
  }

  // When a search query is active, search the ENTIRE library — ignore the
  // category/trade/keyword narrowing from the left panel so items are never
  // "missing" just because a different category is selected. The unit filter
  // still applies. Clearing the search restores the normal category view.
  const selectedSection = selectedSectionId ? orderedSections.find((s) => s.id === selectedSectionId) ?? null : null;
  const selectedSectionLabel = selectedSection ? getNavSectionDisplayLabel(selectedSection, headingNames) : fallbackSectionLabel;
  const selectedItem = selectedSection?.items.find(item => item.id === selectedItemId);
  const selectedItemLabel = selectedSection && selectedItem ? getNavItemDisplayLabel(selectedSection.id, selectedItem, headingNames) : "";

  function handleEditName(sectionId: string, itemId?: string) {
    setEditingHeading(getNavHeadingEntry(sectionId, itemId));
  }

  const isGlobalSearch = searchQuery.trim().length > 0;

  const filteredTasks = useMemo(() => {
    if (!tasks) return [];
    const filtered = tasks.filter((task) => {
      if (unitFilter !== "all" && task.unit !== unitFilter) return false;
      if (isGlobalSearch) {
        const q = searchQuery.trim().toLowerCase();
        const matchesName = task.name.toLowerCase().includes(q);
        const matchesDesc = task.description?.toLowerCase().includes(q);
        const matchesKeywords = task.keywords?.toLowerCase().includes(q);
        return Boolean(matchesName || matchesDesc || matchesKeywords);
      }
      if (selectedSection) {
        return selectedItem
          ? matchesCostNavItem(task, selectedItem)
          : matchesCostNavSection(task, selectedSection);
      }
      if (selectedCategory !== "all" && task.category !== selectedCategory) return false;
      if (selectedTrade !== "all" && task.trade !== selectedTrade) return false;
      if (navKeyword) {
        const keywords = navKeyword.toLowerCase().split(" ");
        const taskText = `${task.name} ${task.description || ""} ${task.keywords || ""}`.toLowerCase();
        const matched = keywords.some((kw) => taskText.includes(kw));
        if (!matched) return false;
      }
      return true;
    });
    const visible = filtered.filter(t => !t.isHidden);
    const hidden = filtered.filter(t => t.isHidden);
    return [...visible, ...hidden];
  }, [tasks, selectedCategory, selectedTrade, unitFilter, searchQuery, navKeyword, selectedSection, selectedItem]);

  const taskNavLabels = useMemo(() => new Map(filteredTasks.map(task => {
    const browseLocation = isGlobalSearch ? getCostTaskBrowseLocation(task, orderedSections) : null;
    return [
      task.id, getCostTaskDisplayLabels(task, headingNames, {
        sectionLabel: getCategoryLabel(task.category, marketUser?.market),
        itemLabel: getTradeLabel(task.category, task.trade, marketUser?.market),
      }, isGlobalSearch
        ? browseLocation ? { sectionId: browseLocation.section.id, itemId: browseLocation.item.id } : undefined
        : { sectionId: selectedSectionId, itemId: selectedItemId }, orderedSections),
    ] as const;
  })), [filteredTasks, headingNames, marketUser?.market, isGlobalSearch, selectedSectionId, selectedItemId, orderedSections]);

  function getNavLabels(task: CostLibraryTask) {
    return taskNavLabels.get(task.id) ?? getCostTaskDisplayLabels(task, headingNames, {
      sectionLabel: getCategoryLabel(task.category, marketUser?.market),
      itemLabel: getTradeLabel(task.category, task.trade, marketUser?.market),
    });
  }

  function openEditDialog(task: CostLibraryTask) {
    setEditingTask(task);
    overrideForm.reset({
      rateLow: task.rateLow,
      rateTypical: task.rateTypical,
      rateHigh: task.rateHigh,
      changeReason: "",
    });
  }

  function handleSaveOverride(data: z.infer<typeof overrideFormSchema>) {
    if (!editingTask) return;
    if (isRecipeBacked(editingTask, isAdmin) && Number(data.rateTypical) !== Number(editingTask.rateTypical)) {
      setRateConfirmation({ taskId: editingTask.id, source: editingTask.source === "custom" ? "custom" : "default",
        name: editingTask.name, ...data });
      return;
    }
    if (editingTask.source === "custom") {
      updateCustomMutation.mutate({
        id: editingTask.id,
        rateLow: data.rateLow,
        rateTypical: data.rateTypical,
        rateHigh: data.rateHigh,
      });
    } else if (isAdmin) {
      adminSaveMutation.mutate({
        taskId: editingTask.id,
        rateLow: data.rateLow,
        rateTypical: isRecipeBacked(editingTask, isAdmin) ? undefined : data.rateTypical,
        rateHigh: data.rateHigh,
      });
    } else {
      saveMutation.mutate({
        taskId: editingTask.id,
        rateLow: data.rateLow,
        rateTypical: isRecipeBacked(editingTask, isAdmin) ? undefined : data.rateTypical,
        rateHigh: data.rateHigh,
        changeReason: data.changeReason,
      });
    }
  }

  function handleResetToDefault() {
    if (!editingTask?.overrideId || !allowsGenericOverrideReset(editingTask)) return;
    resetMutation.mutate(editingTask.overrideId);
  }

  return (
    <div className="flex h-full" data-testid="page-cost-library">
      <NavigationHeadingNameDialog entry={editingHeading} onOpenChange={open => { if (!open) setEditingHeading(null); }} />
      {/* Desktop sidebar */}
      <div className="w-72 border-r bg-card/50 flex-shrink-0 hidden md:flex flex-col">
        <CategorySidebar
          sections={orderedSections}
          sidebarSearch={sidebarSearch}
          setSidebarSearch={setSidebarSearch}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
          selectedItemId={selectedItemId}
          onSelectItem={handleSelectItem}
          onSelectSection={handleSelectSection}
          onDragEnd={reorderSections}
          headingNames={headingNames}
          onEditName={handleEditName}
        />
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="border-b p-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            {/* Mobile categories sheet button */}
            <div className="md:hidden w-full mb-1">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full gap-2" data-testid="button-mobile-categories">
                    <SlidersHorizontal className="h-4 w-4" />
                    {selectedSectionLabel ? selectedSectionLabel : "Browse Categories"}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-80">
                  <CategorySidebar
                    sections={orderedSections}
                    sidebarSearch={sidebarSearch}
                    setSidebarSearch={setSidebarSearch}
                    expandedSections={expandedSections}
                    toggleSection={toggleSection}
                    selectedItemId={selectedItemId}
                    onSelectItem={(item, sectionId) => { handleSelectItem(item, sectionId); }}
                    onSelectSection={(section) => { handleSelectSection(section); }}
                    onDragEnd={reorderSections}
                    headingNames={headingNames}
                    onEditName={handleEditName}
                  />
                </SheetContent>
              </Sheet>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={handleShowAll}
                className="text-lg sm:text-2xl font-bold hover:text-muted-foreground transition-colors"
                data-testid="button-show-all"
              >
                Cost Library
              </button>
              <HelpTip testId="button-help-cost-library" content="Your Cost Library is the single source of truth for pricing. Set your own rates here and they'll override AI prices on all future quotes. Browse by category, search for specific tasks, or add custom items." />
              {isGlobalSearch && (
                <>
                  <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm font-medium truncate" data-testid="text-search-indicator">
                    Search results for &ldquo;{searchQuery.trim()}&rdquo;
                  </span>
                </>
              )}
              {!isGlobalSearch && selectedSectionLabel && (
                <>
                  <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                  <button
                    onClick={() => {
                      if (selectedSection) handleSelectSection(selectedSection);
                    }}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {selectedSectionLabel}
                  </button>
                </>
              )}
              {!isGlobalSearch && selectedItemLabel && (
                <>
                  <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm font-medium truncate" data-testid="text-active-item">
                    {selectedItemLabel}
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button
                  variant="outline"
                  onClick={() => { setPushPreview(null); setShowPushLiveDialog(true); pushLiveMutation.mutate(true); }}
                  data-testid="button-update-live"
                >
                  <UploadCloud className="h-4 w-4 mr-2" />
                  Update Live
                </Button>
              )}
              <Button onClick={() => setShowCustomDialog(true)} data-testid="button-add-custom-task">
                <Plus className="h-4 w-4 mr-2" />
                Add Custom Task
              </Button>
            </div>
          </div>
        </div>

        <div className="px-4 pt-4">
          <TutorialTip id="cost-library-intro" title="Your Personal Rate Card">
            The Cost Library holds default UK rates for every trade. Browse by category in the left panel, then click any item to override its rate with what you actually charge. Overridden rates are applied automatically in every future Auto Quote. Use <strong>Add Custom Task</strong> to add specialist items that aren't in the default list.
          </TutorialTip>
        </div>

        <div className="p-4 border-b">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="input-search-tasks"
              />
            </div>
            <div className="md:hidden">
              <Select value={selectedCategory} onValueChange={(v) => { setSelectedCategory(v); setSelectedTrade("all"); setNavKeyword(""); setSelectedItemId(null); setSelectedSectionId(null); setFallbackSectionLabel(v === "all" ? "" : (COST_CATEGORIES.find(c => c.value === v)?.label || v)); }}>
                <SelectTrigger className="w-[200px]" data-testid="select-category-filter-mobile">
                  <Layers className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {COST_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Select value={unitFilter} onValueChange={setUnitFilter}>
              <SelectTrigger className="w-[140px]" data-testid="select-unit-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Unit" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Units</SelectItem>
                {UNIT_OPTIONS.map((u) => (
                  <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedItemId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShowAll}
                data-testid="button-clear-filter"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="p-6"><TableSkeleton rows={10} /></div>
          ) : libraryError ? (
            <div role="alert" className="space-y-3 p-6 text-sm">
              <p>{libraryError.message}</p>
              <Button type="button" variant="outline" onClick={() => void refetchLibrary()}>Retry library</Button>
            </div>
          ) : !selectedSectionId && !selectedItemId && selectedCategory === "all" && !isGlobalSearch ? (
            <EmptyState
              icon={BookOpen}
              title="Select a category"
              description="Choose a category from the left panel to browse its rates and set your own."
            />
          ) : !filteredTasks.length ? (
            <EmptyState
              icon={BookOpen}
              title="No tasks found"
              description="Try adjusting your search or filters, or select a different category."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead className="text-right">Low</TableHead>
                  <TableHead className="text-right">Typical</TableHead>
                  <TableHead className="text-right">High</TableHead>
                  <TableHead className="text-center">Source</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTasks.map((task) => {
                  const breakdownKey = `${task.source}-${task.id}`;
                  const selectedCostSection = expandedBreakdowns[breakdownKey];
                  const breakdownOpen = isUkMarket && !!selectedCostSection;
                  return (
                  <Fragment key={breakdownKey}>
                  <TableRow
                    data-testid={`row-task-${task.id}`}
                    className={task.isHidden ? "opacity-40" : ""}
                  >
                    <TableCell>
                      <div className={`font-medium ${task.isHidden ? "line-through" : ""}`} data-testid={`text-task-name-${task.id}`}>{task.name}</div>
                      {task.description && (
                        <div className="text-sm text-muted-foreground truncate max-w-xs">{task.description}</div>
                      )}
                      <div className="text-xs text-muted-foreground mt-0.5">{getNavLabels(task).sectionLabel}</div>
                      {isUkMarket && !task.isHidden && (
                        <div className="mt-1 flex gap-1" role="group" aria-label={`Cost sections for ${task.name}`}>
                          {(["labour", "materials", "plant"] as const).map(section => {
                            const Icon = section === "labour" ? HardHat : section === "materials" ? StackedBricksIcon : TowerCraneIcon;
                            const active = selectedCostSection === section;
                            return <Button key={section} type="button" size="icon" variant={active ? "secondary" : "outline"}
                              className={`h-8 w-8 rounded-md p-0 ${active ? "border border-primary text-primary bg-primary/10" : "text-muted-foreground"}`}
                              onClick={() => toggleBreakdown(breakdownKey, section)}
                              title={COST_SECTION_LABELS[section]}
                              aria-label={`${COST_SECTION_LABELS[section]} for ${task.name}`} aria-expanded={active}
                              aria-controls={`cost-section-${breakdownKey}`}
                              data-testid={`button-toggle-${section}-${task.id}`}>
                              <Icon className="h-4 w-4" aria-hidden="true" />
                            </Button>;
                          })}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => handleGoToTaskCategory(task)}
                        title="Go to this category"
                        data-testid={`button-goto-category-${task.id}`}
                      >
                        <Badge variant="secondary" className="font-normal cursor-pointer hover:bg-secondary/70 transition-colors">
                          {getNavLabels(task).itemLabel}
                        </Badge>
                      </button>
                    </TableCell>
                    <TableCell>{getUnitLabel(task.unit)}</TableCell>
                    <TableCell className="text-right text-muted-foreground" data-testid={`text-rate-low-${task.id}`}>
                      {formatCurrency(task.rateLow)}
                    </TableCell>
                    <TableCell className="text-right font-medium" data-testid={`text-rate-typical-${task.id}`}>
                      {formatCurrency(task.rateTypical)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground" data-testid={`text-rate-high-${task.id}`}>
                      {formatCurrency(task.rateHigh)}
                    </TableCell>
                    <TableCell className="text-center">
                      {task.isHidden ? (
                        <Badge variant="outline" className="text-muted-foreground" data-testid={`badge-source-${task.id}`}>Hidden</Badge>
                      ) : task.source === "override" ? (
                        <Badge variant="default" data-testid={`badge-source-${task.id}`}>My Rate</Badge>
                      ) : task.source === "custom" ? (
                        <Badge variant="outline" data-testid={`badge-source-${task.id}`}>Custom</Badge>
                      ) : (task.isPriceLocked || task.isAdminEdited) && isAdmin ? (
                        // Admin-set rates are just "Default" from a normal user's
                        // point of view; only admins see the teal identifier.
                        <Badge className="text-[10px] bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200 border-0" data-testid={`badge-source-${task.id}`}>Admin Rate</Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">Default</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {task.isHidden ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => unhideMutation.mutate(task.id)}
                            disabled={unhideMutation.isPending}
                            data-testid={`button-restore-task-${task.id}`}
                            title="Restore task"
                          >
                            <Undo2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => openEditDialog(task)}
                              data-testid={`button-edit-task-${task.id}`}
                              title="Edit rates"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => hideMutation.mutate({ taskId: task.id, taskSource: task.source })}
                              disabled={hideMutation.isPending}
                              data-testid={`button-hide-task-${task.id}`}
                              title="Hide task"
                            >
                              <EyeOff className="h-4 w-4" />
                            </Button>
                            {!(task.source === "override" && (task as { hasRecipeOverride?: boolean }).hasRecipeOverride === true) && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => {
                                if (task.source === "custom") {
                                  deleteCustomMutation.mutate(task.id);
                                } else if (allowsGenericOverrideReset(task) && task.overrideId) {
                                  resetMutation.mutate(task.overrideId);
                                }
                              }}
                              disabled={deleteCustomMutation.isPending || resetMutation.isPending || (task.source === "default") || (task.source === "override" && !allowsGenericOverrideReset(task))}
                              data-testid={`button-remove-task-${task.id}`}
                              title={task.source === "custom" ? "Delete task" : task.source === "override" ? "Reset to default rate" : "Use Hide to remove default tasks"}
                              className={task.source === "default" ? "opacity-30 cursor-not-allowed" : ""}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {breakdownOpen && !task.isHidden && (
                    <TableRow className="hover:bg-transparent" data-testid={`row-breakdown-${task.id}`}>
                      <TableCell colSpan={8} className="bg-muted/30 p-0">
                        <div id={`cost-section-${breakdownKey}`} className="sticky left-0 w-[calc(100vw-2rem)] max-w-3xl p-4 md:w-auto">
                          <CostRecipePanel
                            section={selectedCostSection}
                            taskId={task.id}
                            source={task.source === "custom" ? "custom" : "default"}
                            unitLabel={getUnitLabel(task.unit)}
                            isAdmin={isAdmin}
                            formatCurrency={formatCurrency}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {filteredTasks.length > 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Showing {filteredTasks.filter(t => !t.isHidden).length} of {tasks?.filter(t => !t.isHidden).length || 0} tasks
              {filteredTasks.some(t => t.isHidden) && (
                <span className="ml-1">({filteredTasks.filter(t => t.isHidden).length} hidden)</span>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Update Live — preview the dev→live cost library diff, then push it. */}
      <Dialog open={showPushLiveDialog} onOpenChange={(open) => { if (!open) { setShowPushLiveDialog(false); setPushPreview(null); } }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Update Live Cost Library</DialogTitle>
            <p className="text-sm text-muted-foreground pt-1">
              Pushes your saved price edits and owner-reviewed breakdowns to the live site after confirmation. Initial breakdown additions keep existing prices. More recent live edits are left alone.
            </p>
          </DialogHeader>
          {pushLiveMutation.isPending ? (
            <div className="flex items-center gap-2 py-6 justify-center text-sm text-muted-foreground" data-testid="text-push-live-loading">
              <Loader2 className="h-4 w-4 animate-spin" />
              {pushPreview ? "Pushing to live…" : "Comparing with the live site…"}
            </div>
          ) : pushPreview ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <Card><CardContent className="p-3"><div className="text-2xl font-bold" data-testid="text-push-updates">{pushPreview.updates}</div><div className="text-xs text-muted-foreground">Task updates</div></CardContent></Card>
                <Card><CardContent className="p-3"><div className="text-2xl font-bold" data-testid="text-push-inserts">{pushPreview.inserts}</div><div className="text-xs text-muted-foreground">New tasks</div></CardContent></Card>
                <Card><CardContent className="p-3"><div className="text-2xl font-bold" data-testid="text-push-deactivates">{pushPreview.deactivates}</div><div className="text-xs text-muted-foreground">Removed</div></CardContent></Card>
              </div>
              {pushPreview.skippedNewerOnTarget > 0 && (
                <p className="text-xs text-muted-foreground">
                  {pushPreview.skippedNewerOnTarget} task{pushPreview.skippedNewerOnTarget === 1 ? " was" : "s were"} edited more recently on the live site and will be left alone.
                </p>
              )}
              <p className="text-xs text-muted-foreground">{pushPreview.unchanged ?? 0} unchanged · {pushPreview.conflicts ?? 0} conflicts left untouched.</p>
              {pushPreview.dryRun && pushPreview.updates > 0 && (
                <p className="text-xs text-muted-foreground" data-testid="text-push-live-scope">
                  This includes all pending updates listed below, not just your last edit. Earlier Price Review changes may also be included. Latest edits are listed first.
                </p>
              )}
              {!!pushPreview.conflicts && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-950 max-h-40 overflow-y-auto" data-testid="text-push-conflicts">
                  <p className="font-medium mb-1">These items need separate attention:</p>
                  {pushPreview.conflictDetails?.map((conflict, i) => <p key={i}>{conflict.name}: {conflict.reason}</p>)}
                </div>
              )}
              {(pushPreview.updateDetails.length > 0 || pushPreview.insertDetails.length > 0 || pushPreview.deactivateDetails.length > 0) && (
                <ScrollArea className="max-h-64 border rounded-md">
                  <div className="p-3 space-y-1 text-sm">
                    {pushPreview.updateDetails.map((u, i) => (
                      <div key={`u-${i}`} className="flex justify-between gap-3">
                        <span className="truncate">{u.name} <span className="text-xs text-muted-foreground uppercase">{u.market}</span></span>
                        <span className="text-muted-foreground text-right">
                          {u.changedFields.some(f => ["rateLow", "rateTypical", "rateHigh"].includes(f))
                            ? <>Price update · {formatCurrency(u.oldRateTypical)} → <span className="text-foreground font-medium">{formatCurrency(u.newRateTypical)}</span>{Number(u.oldRateTypical) === Number(u.newRateTypical) ? " (Low/High changed)" : ""}</>
                            : u.changedFields.includes("recipe")
                              ? <>Breakdown only · {formatCurrency(u.newRateTypical)} (price unchanged)</>
                              : <>Library details · {formatCurrency(u.newRateTypical)} (price unchanged)</>}
                        </span>
                      </div>
                    ))}
                    {pushPreview.insertDetails.map((r, i) => (
                      <div key={`i-${i}`} className="flex justify-between gap-3">
                        <span className="truncate">{r.name} <span className="text-xs text-muted-foreground uppercase">{r.market}</span></span>
                        <span className="text-emerald-600 dark:text-emerald-400 whitespace-nowrap">new · {formatCurrency(r.rateTypical)}</span>
                      </div>
                    ))}
                    {pushPreview.deactivateDetails.map((d, i) => (
                      <div key={`d-${i}`} className="flex justify-between gap-3">
                        <span className="truncate line-through text-muted-foreground">{d.name} <span className="text-xs uppercase">{d.market}</span></span>
                        <span className="text-muted-foreground">removed</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
              {pushPreview.dryRun && pushPreview.updates + pushPreview.inserts + pushPreview.deactivates === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="text-push-live-in-sync">{pushPreview.conflicts || pushPreview.skippedNewerOnTarget ? "No eligible updates. Review the conflicts or newer live edits above." : "The live site is already in sync — nothing to push."}</p>
              )}
              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button variant="outline" onClick={() => { setShowPushLiveDialog(false); setPushPreview(null); }}>
                  {pushPreview.dryRun ? "Cancel" : "Close"}
                </Button>
                {pushPreview.dryRun && pushPreview.updates + pushPreview.inserts + pushPreview.deactivates > 0 && (
                  <Button onClick={() => pushLiveMutation.mutate(false)} disabled={!pushPreview.planToken} data-testid="button-confirm-push-live">
                    <UploadCloud className="h-4 w-4 mr-2" />
                    Push to Live
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingTask} onOpenChange={(open) => { if (!open) setEditingTask(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTask?.source === "custom" ? "Edit Custom Task" : "Edit Rate"} - {editingTask?.name}</DialogTitle>
            {editingTask && (
              <p className="text-sm text-muted-foreground pt-1">
                {getNavLabels(editingTask).itemLabel} / {getNavLabels(editingTask).sectionLabel} / {getUnitLabel(editingTask.unit)}
              </p>
            )}
          </DialogHeader>
          {editingTask && (
            <div className="space-y-5">
              {isAdmin && editingTask.source !== "custom" && (
                <div className="rounded-md bg-teal-50 dark:bg-teal-950 border border-teal-200 dark:border-teal-800 px-3 py-2 text-sm text-teal-800 dark:text-teal-200" data-testid="text-admin-rate-note">
                  You're editing the central <strong>Admin Rate</strong> — it becomes the default for all users (their own My Rates still take priority).
                </div>
              )}
              {editingTask.source === "override" && editingTask.defaultRateTypical && (
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">Default rates:</p>
                    <div className="flex gap-6 text-sm flex-wrap">
                      <span>Low: {formatCurrency(editingTask.defaultRateLow!)}</span>
                      <span>Typical: {formatCurrency(editingTask.defaultRateTypical!)}</span>
                      <span>High: {formatCurrency(editingTask.defaultRateHigh!)}</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Form {...overrideForm}>
                <form onSubmit={overrideForm.handleSubmit(handleSaveOverride)} className="space-y-5">
                  <div className="grid grid-cols-3 gap-4">
                    <FormField
                      control={overrideForm.control}
                      name="rateLow"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Low</FormLabel>
                          <FormControl>
                            <Input {...field} type="number" step="0.01" data-testid="input-rate-low" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={overrideForm.control}
                      name="rateTypical"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Typical</FormLabel>
                          <FormControl>
                        <Input {...field} type="number" step="0.01" min="0" data-testid="input-rate-typical" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={overrideForm.control}
                      name="rateHigh"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>High</FormLabel>
                          <FormControl>
                            <Input {...field} type="number" step="0.01" data-testid="input-rate-high" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  {isRecipeBacked(editingTask, isAdmin) && (
                    <p className="text-sm text-muted-foreground rounded-md border px-3 py-2" data-testid="text-typical-from-breakdown">
                      Changing Typical will ask you to confirm matching adjustments to the breakdown. Editing Labour, Materials or Plant updates all three rates proportionally. Low and High can also be edited individually here.
                    </p>
                  )}
                  {editingTask.source !== "custom" && !isAdmin && (
                    <FormField
                      control={overrideForm.control}
                      name="changeReason"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Reason for change (optional)</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="e.g. Based on recent project costs" data-testid="input-change-reason" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                  {allowsGenericOverrideReset(editingTask) && (
                    <div className="pt-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleResetToDefault}
                        disabled={resetMutation.isPending}
                        className="text-muted-foreground"
                        data-testid="button-reset-default"
                      >
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                        Reset to Default
                      </Button>
                    </div>
                  )}
                  <div className="flex justify-end gap-3 pt-2 border-t">
                    <Button type="button" variant="outline" onClick={() => setEditingTask(null)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={saveMutation.isPending || updateCustomMutation.isPending || adminSaveMutation.isPending} data-testid="button-save-rate">
                      <Save className="h-4 w-4 mr-2" />
                      {(saveMutation.isPending || updateCustomMutation.isPending || adminSaveMutation.isPending) ? "Saving..." : editingTask.source === "custom" ? "Save Changes" : isAdmin ? "Save Admin Rate" : "Save Rate"}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {rateConfirmation && <CostRecipeRateConfirmation request={rateConfirmation}
        formatCurrency={formatCurrency} onClose={() => setRateConfirmation(null)}
        onSaved={() => {
          setRateConfirmation(null); setEditingTask(null); overrideForm.reset();
          toast({ title: isAdmin ? "Admin rate and breakdown saved" : "Rate and breakdown saved" });
        }} />}

      <Dialog open={showCustomDialog} onOpenChange={setShowCustomDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Custom Task</DialogTitle>
          </DialogHeader>
          <Form {...customForm}>
            <form onSubmit={customForm.handleSubmit((data) => createCustomMutation.mutate(data))} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={customForm.control}
                  name="category"
                  render={() => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <Select
                        onValueChange={(sectionId) => {
                          setCustomNavSection(sectionId);
                          customForm.setValue("category", "");
                          customForm.setValue("trade", "");
                        }}
                        value={customNavSection}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-custom-category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {orderedSections.map((section) => (
                            <SelectItem key={section.id} value={section.id}>{getNavSectionDisplayLabel(section, headingNames)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={customForm.control}
                  name="trade"
                  render={() => (
                    <FormItem>
                      <FormLabel>Subcategory</FormLabel>
                      <Select
                        onValueChange={(itemId) => {
                          const section = orderedSections.find(s => s.id === customNavSection);
                          const navItem = section?.items.find(i => i.id === itemId);
                          if (navItem) {
                            customForm.setValue("category", navItem.categoryFilter || "other");
                            customForm.setValue("trade", navItem.tradeFilter || navItem.categoryFilter || "other");
                          }
                        }}
                        value={(() => {
                          const section = orderedSections.find(s => s.id === customNavSection);
                          const cat = customForm.getValues("category");
                          const trade = customForm.getValues("trade");
                          const match = section?.items.find(i => i.categoryFilter === cat && i.tradeFilter === trade);
                          return match?.id || "";
                        })()}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-custom-trade">
                            <SelectValue placeholder="Select subcategory" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(() => {
                            const section = orderedSections.find(s => s.id === customNavSection);
                            if (section) {
                              return section.items.map((item) => (
                                <SelectItem key={item.id} value={item.id}>{getNavItemDisplayLabel(section.id, item, headingNames)}</SelectItem>
                              ));
                            }
                            return <SelectItem value="_none" disabled>Select a category first</SelectItem>;
                          })()}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={customForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Task Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. Install underfloor heating" data-testid="input-custom-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={customForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (optional)</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Short description of the task" className="resize-none" data-testid="input-custom-description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-4 gap-3">
                <FormField
                  control={customForm.control}
                  name="unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-custom-unit">
                            <SelectValue placeholder="Unit" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {UNIT_OPTIONS.map((u) => (
                            <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={customForm.control}
                  name="rateLow"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Low</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" step="0.01" data-testid="input-custom-rate-low" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={customForm.control}
                  name="rateTypical"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Typical</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" step="0.01" data-testid="input-custom-rate-typical" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={customForm.control}
                  name="rateHigh"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>High</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" step="0.01" data-testid="input-custom-rate-high" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={customForm.control}
                name="keywords"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Search Keywords (optional)</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Comma-separated keywords" data-testid="input-custom-keywords" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => { setShowCustomDialog(false); setCustomNavSection(""); }}>Cancel</Button>
                <Button type="submit" disabled={createCustomMutation.isPending} data-testid="button-save-custom-task">
                  {createCustomMutation.isPending ? "Saving..." : "Add Task"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
